
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Define the invitation type to match our database response
interface Invitation {
  id: string;
  email: string;
  name: string | null;
  role: string;
  token: string;
  created_at: string;
  expires_at: string;
  used_at: string | null;
}

const InvitePage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<any>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract token from URL query params
  const query = new URLSearchParams(location.search);
  const token = query.get('token');

  // Check if user is already authenticated
  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      setCheckingAuth(false);
    };
    
    checkSession();
  }, []);

  // Validate invitation token
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError("Ungültiger Einladungslink. Kein Token gefunden.");
        setValidating(false);
        return;
      }

      try {
        // Call database function to get invitation by token
        const { data, error } = await supabase.from('user_invitations')
          .select('*')
          .eq('token', token)
          .single();
        
        if (error || !data) {
          console.error("Error validating invitation:", error);
          setError("Ungültiger Einladungslink oder Einladung abgelaufen.");
          setValidating(false);
          return;
        }
        
        // Check if invitation is expired
        if (new Date(data.expires_at) < new Date()) {
          setError("Diese Einladung ist abgelaufen.");
          setValidating(false);
          return;
        }
        
        // Check if invitation has been used
        if (data.used_at) {
          setError("Diese Einladung wurde bereits verwendet.");
          setValidating(false);
          return;
        }
        
        console.log("Valid invitation found:", data);
        setInvitation(data as Invitation);
        setValidating(false);
      } catch (err) {
        console.error("Error fetching invitation:", err);
        setError("Fehler beim Abrufen der Einladungsinformationen.");
        setValidating(false);
      }
    };

    if (!checkingAuth && !session) {
      validateToken();
    } else {
      setValidating(false);
    }
  }, [token, checkingAuth, session]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!invitation) {
      toast.error("Ungültige Einladung.");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwörter stimmen nicht überein.");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    
    setLoading(true);
    
    try {
      // First, try to sign up the user
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: invitation.email,
        password
      });
      
      if (signUpError) {
        if (signUpError.message.includes("already registered")) {
          // If user exists, try to sign in with the new password
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email: invitation.email,
            password
          });
          
          if (signInError) {
            console.error("Error signing in:", signInError);
            toast.error(`Fehler: ${signInError.message}`);
            setLoading(false);
            return;
          }
        } else {
          console.error("Error signing up:", signUpError);
          toast.error(`Fehler: ${signUpError.message}`);
          setLoading(false);
          return;
        }
      }
      
      // Mark invitation as used
      await supabase
        .from("user_invitations")
        .update({ used_at: new Date().toISOString() })
        .eq("token", token!);
      
      toast.success("Willkommen! Sie sind jetzt angemeldet.");
      navigate("/");
    } catch (err) {
      console.error("Error processing invitation:", err);
      toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  // If checking authentication, show loading
  if (checkingAuth) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-[#0A1118]">
        <Card className="w-full max-w-md bg-[#10161D] border-gray-700 text-white">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-white text-center">
              Wird geladen...
            </CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center p-6">
            <div className="animate-spin h-8 w-8 border-4 border-t-[#41c7f0] border-gray-700 rounded-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Redirect if already authenticated
  if (session) {
    return <Navigate to="/" />;
  }

  if (validating) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-[#0A1118]">
        <Card className="w-full max-w-md bg-[#10161D] border-gray-700 text-white">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-white text-center">
              Einladung wird geprüft...
            </CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center p-6">
            <div className="animate-spin h-8 w-8 border-4 border-t-[#41c7f0] border-gray-700 rounded-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-[#0A1118]">
        <Card className="w-full max-w-md bg-[#10161D] border-gray-700 text-white">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-white text-center">
              Fehler
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <CardDescription className="text-gray-300 text-center mb-4">
              {error}
            </CardDescription>
            <div className="flex justify-center">
              <Button 
                onClick={() => navigate("/login")}
                className="bg-[#41c7f0] text-white hover:bg-[#3ab1d9]"
              >
                Zum Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-[#0A1118]">
      <Card className="w-full max-w-md bg-[#10161D] border-gray-700 text-white">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-white text-center">
            Willkommen bei HOSS-IMMO
          </CardTitle>
          <CardDescription className="text-gray-300 text-center">
            Setzen Sie ein Passwort für Ihren Account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invitation && (
            <div className="mb-6 text-center">
              <p className="text-gray-300 mb-1">
                Einladung für:
              </p>
              <p className="text-xl text-white font-medium mb-1">
                {invitation.name || invitation.email.split('@')[0]}
              </p>
              <p className="text-gray-400">
                {invitation.email}
              </p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password">Passwort</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#1A2433] border-gray-700 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-[#1A2433] border-gray-700 text-white"
                required
              />
            </div>
          </form>
        </CardContent>
        <CardFooter>
          <Button 
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-[#41c7f0] text-white hover:bg-[#3ab1d9]"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin h-4 w-4 border-2 border-t-white border-r-white border-b-transparent border-l-transparent rounded-full mr-2" />
                Verarbeitung...
              </div>
            ) : (
              'Account fertigstellen'
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default InvitePage;
